import { ReadonlyVec2 } from 'gl-matrix';
import { PanZoom } from '../../../view/PanZoom';
import { View } from '../../../view/View';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { DrawingTool } from './DrawingTool';
import { DrawAction, DrawActionState } from '../../actions/DrawAction';
import { Orientations, PlaneView } from '~/engine/view/views/PlaneView';
import { BrushCursorAction } from '../../actions/contouring-actions/BrushCursorAction';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { enqueueSnackbar } from 'notistack';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomstructureset';

/**
 * {@link BrushTool} is a {@link DrawingTool} that allows the user to draw SDFs.\
 * It is possible to introduce different types of brushes, by extending this class and overriding the {@link BrushTool.createCursorAction} and {@link BrushTool.createDrawAction} methods.\
 *
 * Additionally {@link BrushTool} inherits from {@link DrawingTool} and thus provides {@link PanAction} and {@link ZoomAction} functionality.
 */
export abstract class BrushTool extends DrawingTool {
    public readonly toolType = 'brush';
    /**
     * Brush modifier keys
     */
    public static readonly BRUSH_SIZE_MODIFIER_KEYS = ModifierKeys.SHIFT;

    public static readonly BRUSH_ERASE_MODIFIER_KEYS = ModifierKeys.ALT;

    /**
     * Maximum diameter of the brush in millimeters.
     */
    public static readonly MAX_BRUSH_SIZE = 40;

    /**
     * Reference to the {@link RegionOfInterest} that is being drawn on.
     */
    protected readonly structure: RegionOfInterest;

    /**
     * Method to create a new {@link BrushTool} instance.\
     * The {@link BrushTool} is initialized with 4 actions;\
     * {@link PanAction}, {@link ZoomAction}.
     *
     *
     * @param panZoom Reference to {@link PanZoom}-object, which is used to provide default Panning and Zooming functionality.
     */
    public constructor(
        panZoom: PanZoom,
        erase: boolean,
        diameter: number,
        structure: RegionOfInterest,
        private readonly brushSizeChangedCallback: (brushSize: number) => void
    ) {
        super(panZoom);
        this._erase = erase;
        this._diameter = diameter;
        this.structure = structure;
    }

    /**
     * Method to inform the {@link BrushTool} about {@link onMouseMove}-event.\
     * {@link BrushTool} checks if the {@link BrushTool.BRUSH_SIZE_MODIFIER_KEYS} are pressed and if so, it calls the {@link BrushTool.changeBrushSize}-method.\
     * If the {@link BrushTool.BRUSH_SIZE_MODIFIER_KEYS} are not pressed, it then performs the {@link DrawingTool.onMouseMove}-method.
     * @param delta Either 1 or -1, depending on the direction of the mouse wheel scroll.
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event.
     * @param coordinates {@link ReadonlyVec2 Client pixel coordinates} of the mouse, in between [0, pixelSize - 1], at the time of the event.
     * @param view Optional {@link View} on which the event occured.
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_HANDLED} if the {@link BrushTool.BRUSH_SIZE_MODIFIER_KEYS} were pressed and the {@link BrushTool.brushSize} was changed.\
     * Otherwise it returns the {@link EventHandlerResult} generated by the {@link DrawingTool.onMouseWheel}-method.
     */
    public onMouseWheel(
        delta: 1 | -1,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (modifierKeys.equals(BrushTool.BRUSH_SIZE_MODIFIER_KEYS)) {
            let step: number;

            if (this.diameter <= 5) {
                step = 1;
            } else if (this.diameter <= 10) {
                step = 2;
            } else if (this.diameter <= 20) {
                step = 3;
            } else {
                step = 5;
            }
            this.diameter = Math.max(
                Math.min(
                    this._diameter + delta * step,
                    BrushTool.MAX_BRUSH_SIZE
                ),
                1.0
            );
            this._cursorAction?.updateBrushSize(this._diameter);
            this.brushSizeChangedCallback(this._diameter);
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return this._zoomAction.onMouseWheel(
            delta,
            modifierKeys,
            coordinates,
            view
        );
    }

    protected _cursorAction?: BrushCursorAction;

    protected _drawAction: DrawAction | undefined;

    protected _drawState: DrawActionState | undefined;

    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (
            this.structure.dicomParent.approvalStatus ===
            DICOMApprovalStatus.APPROVED
        ) {
            this.resetDrawAction();
            enqueueSnackbar(
                'Contouring is not allowed for this ROI as it has already been reviewed.',
                { variant: 'warning' }
            );
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (
            button === 0 &&
            view.label === Orientations.TRANSVERSAL_ORIENTATION
        ) {
            if (modifierKeys.equals(BrushTool.BRUSH_ERASE_MODIFIER_KEYS)) {
                this._erase = !this._erase;
            }
            view.snapToImagePixel();
            if (!this._drawAction) {
                this.resetDrawAction();
            }
            this._drawState = this._drawAction?.startAction(view, coordinates);
            this._cursorAction?.stopAction();
            if (modifierKeys.equals(BrushTool.BRUSH_ERASE_MODIFIER_KEYS)) {
                this._erase = !this._erase;
            }
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return this._panAction.onMousePress(
            button,
            modifierKeys,
            coordinates,
            view
        );
    }

    public onMouseRelease(
        button: number,
        coordinates: ReadonlyVec2,
        view: View
    ): EventHandlerResult {
        if (this._drawAction) {
            const ret = this._drawAction.onMouseRelease(
                button,
                coordinates,
                view
            );
            this._drawAction = undefined;
            this._drawState = undefined;
            return ret;
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onDeactivate(): EventHandlerResult {
        if (this._drawAction) {
            const res = this._drawAction?.stopAction();
            this._drawAction = undefined;
            this._drawState = undefined;
            return res;
        }
        this._cursorAction?.stopAction();
        this._cursorAction = undefined;
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    // recreate the draw action
    protected abstract resetDrawAction(): void;

    // recreate the cursor action
    protected abstract resetCursorAction(): void;

    /** Flag for erase mode */
    protected _erase: boolean = false;

    /**
     * Diameter of the brush in millimeters.\
     */
    protected _diameter: number = Number.NaN;

    /**
     * Method to change the size of the brush.\
     */
    public set diameter(diameter: number) {
        if (diameter > 1 && diameter < BrushTool.MAX_BRUSH_SIZE + 1e-4) {
            this._diameter = diameter;
        }
    }

    public get diameter(): number {
        return this._diameter;
    }
}
